/**
 * Add-to-cart surface.
 *
 * Two behaviours, chosen by where the shopper is:
 *
 *   Off the cart page — shows a toast ("Item added to your cart" + View cart / Continue
 *                       shopping). See snippets/cart-notification.liquid.
 *   ON the cart page  — no toast. A placeholder row slides into the item list while the add is in
 *                       flight, then the cart's own item list and totals are re-rendered in place
 *                       and the placeholder is replaced by the real line. A toast telling you
 *                       something was added, while you are looking at the thing it was added to,
 *                       is noise; a stale list is worse.
 *
 * product-form.js drives this through three members, all of which are required:
 *   - getSectionsToRender()  — called BEFORE the fetch, outside any try/catch, to build the
 *                              `sections` param (product-form.js:34-38). If this element is not
 *                              upgraded, that line throws and add-to-cart breaks silently.
 *                              NOTE it reads `.id` off each entry, which is the SECTION id to
 *                              request — not a DOM id. Hence the separate `target` field below.
 *   - setActiveElement(el)   — stores the element that triggered the add, so focus can go back.
 *   - renderContents(state)  — called with the parsed /cart/add response on success.
 *
 * It also does `this.cart.classList.remove("is-empty")` in its finally block, which is a no-op
 * here but harmless.
 */
if (!customElements.get("cart-notification")) {
  customElements.define(
    "cart-notification",
    class CartNotification extends HTMLElement {
      static PLACEHOLDER_CLASS = "cart-item--placeholder";
      // Safety net: product-form.js does not tell us when an add FAILS (it handles the error
      // itself and never calls renderContents), so a placeholder would otherwise sit there
      // forever. Long enough not to fire before a slow-but-successful add lands.
      static PLACEHOLDER_TIMEOUT = 10000;

      // Spinner shown inside a <product-recommendations> while its fetch is in flight.
      //
      // Mirrors the markup in snippets/cart-recommendations.liquid, which only renders server-side —
      // so any block this file creates or re-fetches has to supply its own. Kept here as one
      // definition because two call sites need it (the drawer's first load and the cart page's
      // refresh) and they must produce the same node: global.js clears it by class name via
      // removeLoadingPlaceholder(), so a divergent copy would spin forever on an empty result.
      //
      // Decorative only — no visually-hidden loading text, because the translated string lives in
      // Liquid and neither case is a state the shopper explicitly asked to be told about. The
      // Liquid-rendered one keeps its role="status" and label.
      static LOADING_MARKUP =
        '<div class="cart-recommendations__loading" aria-hidden="true">' +
        '<span class="cart-recommendations__spinner"></span>' +
        "</div>";

      constructor() {
        super();

        this.activeElement = null;
        this.placeholderTimer = null;

        // NB: the close wiring is in connectedCallback, not here. A custom element constructor runs
        // when the opening tag is parsed and is not permitted to inspect its own children, so a
        // querySelectorAll for the close controls can legitimately come back empty depending on how
        // and when the element is upgraded. connectedCallback always runs with the subtree in place.

        // Bound once and attached/detached on open/close, so Escape only closes while visible
        // and we are not holding a document listener for the life of the page.
        this.onKeyUp = (event) => {
          if (event.code === "Escape") this.close();
        };

        // Capture phase: product-form.js calls preventDefault() in its own submit handler, and
        // capture runs before that, so the placeholder goes in regardless of ordering.
        //
        // Delegated from the document because the cross-sell cards are injected with innerHTML
        // after page load — a listener bound to the forms themselves would miss them.
        document.addEventListener(
          "submit",
          (event) => {
            const form =
              event.target &&
              event.target.closest &&
              event.target.closest('form[data-type="add-to-cart-form"]');
            if (form && this.isCartPage) this.showPlaceholder();
          },
          true
        );
      }

      /**
       * Wires up dismissal. Here rather than in the constructor because this is the first point at
       * which the element's children are guaranteed to be present.
       */
      connectedCallback() {
        // The element is only ever rendered once per page, but connectedCallback fires again on any
        // move within the DOM, and double-binding would call close() twice per click.
        if (this.dismissBound) return;
        this.dismissBound = true;

        // One delegated listener on the host, which covers three things at once:
        //
        //   - a click anywhere OUTSIDE the panel. The host is fixed and fills the viewport, so
        //     "outside the drawer" means inside the host but not inside .cart-notification__inner.
        //     Tested by absence of that ancestor rather than by hitting the backdrop specifically,
        //     so it still holds if the backdrop is ever restyled, resized or removed.
        //   - the backdrop itself, for the same reason.
        //   - the close button and Continue shopping, both marked [data-cart-notification-close].
        //
        // Delegation also means the recommendation cards injected into the panel after open() are
        // covered without rebinding: a click on a card is inside .cart-notification__inner, so it
        // correctly does NOT dismiss.
        this.addEventListener("click", (event) => {
          const target = event.target;
          if (!target || !target.closest) return;

          if (target.closest("[data-cart-notification-close]")) {
            this.close();
            return;
          }

          if (!target.closest(".cart-notification__inner")) this.close();
        });
      }

      /** The cart page renders this element; #main-cart-items is what distinguishes it. */
      get isCartPage() {
        return Boolean(document.getElementById("main-cart-items"));
      }

      /**
       * `id` is the SECTION to request; `target` is the DOM element to update, and `selector` the
       * node within it to swap. Off the cart page only the count badge needs refreshing — the
       * toast copy is static.
       *
       * The cart page's section ids are dynamic (template--<id>__cart), so they are read off the
       * `data-id` attributes the same way cart.js does, rather than hardcoded.
       */
      getSectionsToRender() {
        const sections = [{ id: "cart-icon-bubble", target: "cart-icon-bubble" }];
        if (!this.isCartPage) return sections;

        const items = document.getElementById("main-cart-items");
        const footer = document.getElementById("main-cart-footer");

        if (items && items.dataset.id) {
          sections.push({
            id: items.dataset.id,
            target: "main-cart-items",
            selector: ".js-contents",
          });
        }
        if (footer && footer.dataset.id) {
          // Only the subtotal region carries .js-contents in main-cart-footer, so this updates the
          // totals without disturbing the checkout buttons around them.
          sections.push({
            id: footer.dataset.id,
            target: "main-cart-footer",
            selector: ".js-contents",
          });
        }

        return sections;
      }

      getSectionInnerHTML(html, selector = ".shopify-section") {
        const element = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selector);

        return element ? element.innerHTML : null;
      }

      renderContents(parsedState) {
        this.getSectionsToRender().forEach((section) => {
          const parent = document.getElementById(section.target || section.id);
          const html = parsedState.sections && parsedState.sections[section.id];
          if (!parent || !html) return;

          if (!section.selector) {
            const inner = this.getSectionInnerHTML(html, section.selector);
            // Guarded: a null here would blank the cart icon rather than leave a stale count.
            if (inner !== null) parent.innerHTML = inner;
            return;
          }

          // Replace the matching NODE rather than assigning innerHTML, because the selector can
          // point at the parent itself (#main-cart-items IS the .js-contents element) or at a
          // child of it (main-cart-footer's subtotal block). Same approach as cart.js.
          const incoming = new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(section.selector);
          if (!incoming) return;

          const outgoing = parent.matches(section.selector)
            ? parent
            : parent.querySelector(section.selector);

          if (outgoing) outgoing.replaceWith(document.importNode(incoming, true));
          else parent.innerHTML = incoming.outerHTML;
        });

        // Drop the card for the product just added. The recommendations block is fetched once and
        // never re-fetched, so nothing else would take it out of the list — the Liquid-side filter
        // in cart-recommendations.liquid only applies to a fresh render.
        this.removeRecommendationCard(parsedState.product_id);

        // On the cart page the updated list IS the feedback, so no toast. The placeholder is gone
        // already — it lived inside the markup just replaced — but the timer still needs clearing.
        if (this.isCartPage) {
          this.clearPlaceholder();
          return;
        }

        this.renderProduct(parsedState);
        this.renderItemCount();
        this.renderRecommendations(parsedState.product_id);
        this.open();
      }

      /**
       * Builds the recommendations block for the product just added.
       *
       * The <product-recommendations> element is CREATED here rather than sitting in the markup,
       * because its connectedCallback starts fetching `data-url` as soon as it is upgraded. Present
       * from page load it would either fetch with no product id (empty cart) or fetch for whatever
       * was in the cart beforehand — and its IntersectionObserver fires regardless of the drawer
       * being closed, since `visibility: hidden` still counts as intersecting.
       *
       * Recreating it per add also means each add gets fresh recommendations, and the element's own
       * fetch/render logic in global.js is reused untouched.
       */
      renderRecommendations(productId) {
        const host = this.querySelector(
          "[data-cart-notification-recommendations]"
        );
        if (!host) return;

        // Clear whatever the previous add left behind.
        host.textContent = "";

        const template = host.dataset.urlTemplate;
        if (!template || !productId) return;

        const block = document.createElement("product-recommendations");
        block.className = "cart-product-recommendations";

        // Seeded with the spinner BEFORE the element is connected, because connectedCallback starts
        // the fetch — set it afterwards and there is a window in which the block could already have
        // been filled and then overwritten with a spinner that nothing clears.
        //
        // Needed at all because the block is built here rather than rendered by Liquid: the snippet's
        // own spinner only exists in server-rendered output, so without this the drawer shows an
        // empty 50px gap for the length of the round trip and looks like it has finished with
        // nothing to suggest.
        block.innerHTML = CartNotification.LOADING_MARKUP;

        block.dataset.url = template.replace("__PRODUCT_ID__", productId);
        host.appendChild(block);
      }

      /**
       * Puts "(3 items)" after View cart.
       *
       * The count is read out of the cart icon bubble, which renderContents has just re-rendered
       * from the server — its visually-hidden label is `sections.header.cart_count`, already
       * pluralised and translated for the active locale.
       *
       * Not from Liquid, because the drawer's markup is rendered at page load and would be one add
       * out of date. Not from the /cart/add response either: that returns the line item and carries
       * no item_count. And not from the bubble's VISIBLE number, which cart-icon-bubble.liquid omits
       * entirely once the count reaches 100 — a real case on this store, where quantities run into
       * the hundreds.
       *
       * Getting it from the server-rendered string also avoids reimplementing plural rules in JS:
       * French has a `many` form that a naive one/other split would get wrong.
       */
      renderItemCount() {
        const target = this.querySelector("[data-cart-notification-count]");
        if (!target) return;

        const bubble = document.getElementById("cart-icon-bubble");
        // Scoped to .cart-count-bubble: the icon has its own visually-hidden "Cart" label first.
        const source =
          bubble && bubble.querySelector(".cart-count-bubble .visually-hidden");
        const count = source ? source.textContent.trim() : "";

        target.textContent = count ? ` (${count})` : "";
      }

      /**
       * Fills the toast with what was just added — thumbnail, title, and variant/quantity.
       *
       * Built from the /cart/add response, which already carries all of it, rather than fetching
       * Dawn's `cart-notification-product` section. Uses textContent throughout, so a product title
       * cannot inject markup.
       */
      renderProduct(state) {
        const host = this.querySelector("[data-cart-notification-product]");
        if (!host || !state) return;

        host.textContent = "";

        const image = state.featured_image;
        if (image && image.url) {
          const img = document.createElement("img");
          img.className = "cart-notification__product-image";
          // Requested at a thumbnail width — featured_image.url is the FULL-SIZE original (1000px+
          // on this store), which would be a wasteful download for a 56px box.
          img.src = this.thumbnailUrl(image.url, 120);
          img.alt = image.alt || "";
          img.width = 56;
          img.height = 56;
          // NOT loading="lazy". The toast is `visibility: hidden` until open() runs a moment later,
          // and a lazy image inside a hidden container can be deferred indefinitely by the browser —
          // so the row would appear with no thumbnail. It is one 56px image shown immediately, which
          // is exactly the case lazy loading is wrong for.
          img.decoding = "async";
          host.appendChild(img);
        }

        const text = document.createElement("div");
        text.className = "cart-notification__product-text";

        const title = document.createElement("p");
        title.className = "cart-notification__product-title";
        title.textContent = state.product_title || state.title || "";
        text.appendChild(title);

        // Variant only when the product actually has options — otherwise this reads
        // "Default Title". Quantity only when it is more than one, and note it is the LINE total,
        // so adding the same thing twice correctly shows 2.
        const meta = [];
        if (state.variant_title && !state.product_has_only_default_variant) {
          meta.push(state.variant_title);
        }
        if (state.quantity > 1) meta.push(`× ${state.quantity}`);

        if (meta.length) {
          const metaEl = document.createElement("p");
          metaEl.className = "cart-notification__product-meta";
          metaEl.textContent = meta.join(" · ");
          text.appendChild(metaEl);
        }

        host.appendChild(text);
        host.hidden = false;
      }

      /** Adds/replaces a `width` param on a Shopify CDN image URL. */
      thumbnailUrl(url, width) {
        try {
          const parsed = new URL(url, window.location.origin);
          parsed.searchParams.set("width", String(width));
          return parsed.toString();
        } catch (e) {
          // Malformed URL — better the original image than a broken one.
          return url;
        }
      }

      /**
       * Removes a recommendation card by product id.
       *
       * Findable because product-card-cart.liquid keys each form id off the product
       * (`cart-rec-form-<product id>`), which it does anyway to avoid four duplicate ids in one
       * section.
       */
      removeRecommendationCard(productId) {
        if (!productId) return;

        const form = document.getElementById(`cart-rec-form-${productId}`);
        if (!form) return;

        // The <li> is the whole card including its separator; fall back to the card itself in case
        // this snippet is ever rendered outside the list.
        const card = form.closest("li") || form.closest(".product_card--cart");
        (card || form).remove();

        // Emptied the list: pull a fresh set rather than removing the block. The request asks for a
        // bigger pool than it renders (see the limit in cart-recommendations.liquid), so once the
        // Liquid-side filter drops everything now in the cart there are usually more to show.
        const block = document.querySelector(
          "product-recommendations.cart-product-recommendations"
        );
        if (block && !block.querySelector(".product-grid li")) {
          this.refreshRecommendations(block);
        }
      }

      /**
       * Re-fetches the cross-sell block, and only gives up on it if the refresh comes back with
       * nothing — that is the point at which there is genuinely nothing left to suggest.
       */
      refreshRecommendations(block) {
        // Older builds of global.js have no loadRecommendations(); leave the block alone rather
        // than throwing if the two assets are ever out of step.
        if (typeof block.loadRecommendations !== "function") return;

        // A spinner for the gap, since the Liquid-rendered one was replaced by the first load and
        // an empty block during the round trip looks broken.
        block.innerHTML = CartNotification.LOADING_MARKUP;

        block.loadRecommendations().then(() => {
          // Pool exhausted. Remove the block rather than leave a heading above an empty list — on
          // the cart page this also collapses the right-hand column, because the grid's :has() test
          // in cart-page.scss stops finding a .cart-product-recommendations child.
          if (!block.querySelector(".product-grid li")) block.remove();
        });
      }

      /** Skeleton row appended to the cart's item list while the add is in flight. */
      showPlaceholder() {
        const list = document.querySelector("#main-cart-items .cart-items");
        if (!list) return;

        const row = document.createElement("div");
        row.className = `cart-item ${CartNotification.PLACEHOLDER_CLASS}`;
        // Decorative: the real line item announces itself when the list re-renders, and screen
        // readers should not be told about a row that has no content yet.
        row.setAttribute("aria-hidden", "true");
        row.innerHTML =
          '<div class="cart-item__media cart-item--placeholder__media"></div>' +
          '<div class="cart-item__details cart-item--placeholder__details">' +
          '<span class="cart-item--placeholder__line"></span>' +
          '<span class="cart-item--placeholder__line cart-item--placeholder__line--short"></span>' +
          "</div>";

        // Prepended, not appended: this cart orders lines newest-FIRST, so the real item lands at
        // the top of the list when it re-renders. Appending put the placeholder at the bottom and
        // the row then appeared to jump to the top.
        //
        // Verified against /cart.js — adding a second product puts it at index 0. If that ever
        // changes, this is the line to revisit.
        list.prepend(row);

        clearTimeout(this.placeholderTimer);
        this.placeholderTimer = setTimeout(
          () => this.clearPlaceholder(),
          CartNotification.PLACEHOLDER_TIMEOUT
        );
      }

      clearPlaceholder() {
        clearTimeout(this.placeholderTimer);
        this.placeholderTimer = null;
        document
          .querySelectorAll(`.${CartNotification.PLACEHOLDER_CLASS}`)
          .forEach((row) => row.remove());
      }

      open() {
        this.setAttribute("open", "");
        document.addEventListener("keyup", this.onKeyUp);

        // Scroll lock via our own class, NOT the theme's . That one also switches on
        // body::after, a dim overlay sized to BODY — which is max-width 1500px and centred, so it stops
        // short of the viewport edges. This drawer has its own fixed, full-width backdrop.
        document.body.classList.add("cart-notification-open");

        // trapFocus rather than a bare .focus(): this is a modal panel over a backdrop now, so Tab
        // must not wander into the page behind it. It also focuses the element passed, which is
        // what gets the drawer announced — a region hidden until now is not reliably picked up.
        //
        // The close button rather than the first link: it is the least destructive control to land
        // on, and Tab from there reaches View cart and Continue shopping in order.
        //
        // Selected by class, NOT by [data-cart-notification-close]. That attribute is on three
        // elements and the FIRST in document order is the backdrop div — so the old lookup handed
        // trapFocus a bare, unfocusable <div>, meaning focus never entered the panel and the drawer
        // was not announced on open.
        const firstAction = this.querySelector(".cart-notification__close") || this;
        trapFocus(this, firstAction);
      }

      close() {
        this.removeAttribute("open");
        document.removeEventListener("keyup", this.onKeyUp);
        document.body.classList.remove("cart-notification-open");

        // Hands focus back to whatever triggered the add — usually the add-to-cart button — so
        // keyboard users are not dumped at the top of the document. isConnected guards against a
        // trigger that a cart re-render has since replaced.
        const returnTo =
          this.activeElement && this.activeElement.isConnected
            ? this.activeElement
            : null;
        removeTrapFocus(returnTo);
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }
  );
}
